import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  TextField,
  Typography,
  Stack,
  FormControlLabel,
  Switch,
  Paper
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  QrCode2 as QrCodeIcon
} from '@mui/icons-material';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { fetchQRCodeById, updateQRCode, generateQRImageUrl } from '../qrCodeApi';

const EditQRCode = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const [qrCode, setQRCode] = useState(null);
  const [formData, setFormData] = useState({
    IsActive: true,
    Notes: ''
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadQRCode();
  }, [id]);

  const loadQRCode = async () => {
    setLoading(true);
    try {
      const data = await fetchQRCodeById(id);
      setQRCode(data);
      setFormData({
        IsActive: data.IsActive,
        Notes: data.Notes || ''
      });
    } catch (error) {
      Swal.fire(t('error'), t('loadError') || 'Failed to load QR code', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      await updateQRCode(id, formData);
      Swal.fire({
        icon: 'success',
        title: t('success'),
        text: t('qrCodeUpdated') || 'QR code updated successfully'
      });
      navigate('/admin/qrcodes');
    } catch (error) {
      Swal.fire(t('error'), t('updateError') || 'Update failed', 'error');
    }
  };

  if (loading || !qrCode) return null;

  return (
    <Box sx={{ p: 3 }}>
      <Card>
        <CardHeader
          title={
            <Stack direction="row" alignItems="center" spacing={1}>
              <EditIcon sx={{ fontSize: 32, color: 'primary.main' }} />
              <Typography variant="h5" fontWeight="bold">
                {t('editQRCode') || 'Edit QR Code'}
              </Typography>
            </Stack>
          }
          action={
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/admin/qrcodes')}
              sx={{ borderRadius: 50 }}
            >
              {t('back')}
            </Button>
          }
          sx={{ bgcolor: 'background.paper', borderBottom: 1, borderColor: 'divider' }}
        />
        
        <CardContent>
          <Box sx={{ maxWidth: 600, mx: 'auto' }}>
            {/* QR Code Preview */}
            <Paper sx={{ p: 3, mb: 3, textAlign: 'center', bgcolor: 'grey.50' }}>
              <img
                src={generateQRImageUrl(qrCode.TrackingUrl)}
                alt={`QR-${qrCode.Code}`}
                style={{ width: '100%', maxWidth: 200, height: 'auto' }}
              />
              <Typography variant="h6" sx={{ mt: 2, fontFamily: 'monospace', fontWeight: 'bold' }}>
                {qrCode.Code}
              </Typography>
            </Paper>

            {/* Edit Form */}
            <form onSubmit={handleSubmit}>
              <Stack spacing={3}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.IsActive}
                      onChange={(e) => setFormData({ ...formData, IsActive: e.target.checked })}
                      color="primary"
                    />
                  }
                  label={t('activeStatus') || 'Active Status'}
                />

                <TextField
                  label={t('notes') || 'Notes'}
                  multiline
                  rows={4}
                  value={formData.Notes}
                  onChange={(e) => setFormData({ ...formData, Notes: e.target.value })}
                  placeholder={t('addNotesPlaceholder') || 'Add any notes about this QR code...'}
                  fullWidth
                />

                <Stack direction="row" spacing={2} justifyContent="center">
                  <Button
                    variant="outlined"
                    onClick={() => navigate('/admin/qrcodes')}
                    sx={{ borderRadius: 50 }}
                  >
                    {t('cancel')}
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    sx={{ borderRadius: 50 }}
                  >
                    {t('saveChanges')}
                  </Button>
                </Stack>
              </Stack>
            </form>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default EditQRCode;
